<script setup>
import { inject, ref, computed, watch, nextTick, provide } from 'vue';
import { TypeNommage } from '@/scripts/global.js';
import { 
    getErreurNomNorme,
    getErreurTexteRequis } from '@/scripts/controlesSaisie.js';
import { 
	apiModifierListeValeurs } from '@/services/ServiceModele.js';
import Dialog from 'primevue/dialog';
import SaisieTexte from '@/components/generiques/SaisieTexte.vue';


const { listeValeurs } = defineProps({
  listeValeurs: Object
});

const appelerApiModifModele = inject('appelerApiModifModele');
const affichage = inject('affichageModaleEditionValeursListe');
const dupliquerListeValeurs = inject('dupliquerListeValeurs');

const valeurs = ref([]);
const verifErreursSaisie = ref(false);

const erreursSaisie = computed(() => {
    const erreurs = {};
    if (verifErreursSaisie.value) {
        for (let [indiceValeur, valeur] of valeurs.value.entries()) {
            erreurs[`nom_valeur_${indiceValeur}`] = getErreurNomNorme(valeur.nom, TypeNommage.CONSTANTE);
            erreurs[`libelle_valeur_${indiceValeur}`] = getErreurTexteRequis(valeur.libelle);
        }
    }
    return erreurs;
});

const saisieValide = computed(() => {
    return !Object.keys(erreursSaisie.value).map(cle => erreursSaisie.value[cle]).some(erreur => erreur !== null);
});

watch(() => affichage.value, (affichage) => {
    if (affichage) {
        valeurs.value = listeValeurs.valeurs.map(valeur => {
            return {nom: valeur.nom, libelle: valeur.libelle};
        });
        if (valeurs.value.length == 0) {
            ajouterValeur();
        }
    } else {
        verifErreursSaisie.value = false;
    }
})

function monterValeur(valeur) {
    deplacerValeur(valeur, -1);
}

function descendreValeur(valeur) {
    deplacerValeur(valeur, 1);
}

function deplacerValeur(valeur, deplacement) {
    const indice = valeurs.value.indexOf(valeur);
    if (indice + deplacement >= 0 && indice + deplacement < valeurs.value.length) {
        valeurs.value.splice(indice, 1);
        valeurs.value.splice(indice + deplacement, 0, valeur);
    }
}

function supprimerValeur(valeur) {
    const indice = valeurs.value.indexOf(valeur);
    valeurs.value.splice(indice, 1);
}

function ajouterValeur() {
    valeurs.value.push({id: null, nom: '', libelle: ''});
}

function modifierValeursListe() {
    verifErreursSaisie.value = true;
    nextTick(() => {
        if (saisieValide.value) {
            const listeVal = dupliquerListeValeurs(listeValeurs);
            listeVal.valeurs = valeurs.value;
            appelerApiModifModele(apiModifierListeValeurs, listeVal, (reponseOK) => {
                if (reponseOK) {
                    affichage.value = false;
                }
            });
        }        
    });
}

provide('erreursSaisie', erreursSaisie);

</script>

<template>
    <Dialog 
    v-model:visible="affichage"
    :modal="true">
        <template #header>
            <label class="titre">Edition des valeurs de la liste {{ listeValeurs.nom }}</label>
        </template>
        <div>
            <div class="valeurs-liste" @keydown.enter.prevent="modifierValeursListe()">            
                <label class="intitule"></label>
                <label class="intitule">N°</label>
                <label class="intitule">Nom</label>
                <label class="intitule">Libellé</label>
                <label class="intitule"></label>
                <template v-for="(valeur, indiceValeur) in valeurs">
                    <div class="zone-boutons-deplacement">
                        <button 
                            class="bouton btn-deplacement-valeur fa-solid fa-arrow-up" 
                            :disabled="indiceValeur === 0"
                            @click="monterValeur(valeur)" />
                        <button 
                            class="bouton btn-deplacement-valeur fa-solid fa-arrow-down" 
                            :disabled="indiceValeur === valeurs.length - 1"
                            @click="descendreValeur(valeur)" />
                    </div>
                    <label>{{ indiceValeur + 1 }}</label>
                    <SaisieTexte 
                        v-model:valeur="valeur.nom" 
                        :id-erreur="`nom_valeur_${indiceValeur}`" 
                        :autofocus="true" />
                    <SaisieTexte 
                        v-model:valeur="valeur.libelle" 
                        :id-erreur="`libelle_valeur_${indiceValeur}`" />
                    <div>
                        <button class="bouton btn-suppression-valeur fa-solid fa-trash" @click="supprimerValeur(valeur)" />
                    </div>
                </template>
            </div>
            <button class="bouton btn-ajout-valeur fa-solid fa-plus" @click="ajouterValeur()" />
        </div>
        <template #footer>
            <button 
                class="bouton fas fa-check" 
                @click="modifierValeursListe()"/>
        </template>
    </Dialog>
</template>

<style scoped>

.titre {
    margin-right: 10px;
}

.valeurs-liste {
	display: grid;
	grid-template-columns: auto auto auto auto auto;
	gap: 2px 10px;
	align-items: center;
    margin-top: 2px;
}

.btn-deplacement-valeur {
    zoom: 0.8;
    margin-right: 3px;
}

.btn-suppression-valeur {
    zoom: 0.8;
    margin-bottom: 4px;
}

.btn-ajout-valeur {
    zoom: 0.8;
    float: right;
    margin-top: 15px;
    margin-right: 33px;
}

</style>